import { queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RECEIVE_DATA_OBJECTS, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import {
    type ILead,
    type IMergeAsyncExecStatusResult,
    type IPrivateContact,
    type IPrivateContactCompany,
    privateContactProperties
} from '@inpart-io/padme-iap';
import { meetingsManager, privateContactsManager } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { getDobjId } from '@/services/data-object-helper';
import { PRIVATE_CONTACT_COMPANY_MODULE } from '@/store/private-contact-company-module';

import { SET_MATCHES } from './mutations';

export const COUNT_MEETINGS = 'countMeetings';
export const INCREMENT_MEETINGS = 'incrementMeetings';
export const DECREMENT_MEETINGS = 'decrementMeetings';
export const MERGE = 'merge';
export const RETRIEVE_MATCHES = 'retrieveMatches';

function removeDuplicate(list: string[]): string[] {
    const newList = [list[0]];
    for (const el of list) {
        if (!newList.includes(el)) {
            newList.push(el);
        }
    }
    return newList;
}

export const actions: ActionTree<ILead, unknown> = {
    async [COUNT_MEETINGS](context, { contactId }: { contactId: number }): Promise<void> {
        const query = queryBuilder.build(queryBuilder.op.or(queryBuilder.cmp.isIn('contactParticipants', [contactId])));
        const meetingsNb = await meetingsManager.count(query);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: contactId, meetingsNb }] });
    },
    [INCREMENT_MEETINGS](context, { contactId }: { contactId: number }): void {
        const contact = context.getters[GET_BY_ID](contactId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: contactId, meetingsNb: ((contact.meetingsNb as number) ?? 0) + 1 }] });
    },
    [DECREMENT_MEETINGS](context, { contactId }: { contactId: number }): void {
        const contact = context.getters[GET_BY_ID](contactId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: contactId, meetingsNb: ((contact.meetingsNb as number) ?? 0) - 1 }] });
    },
    async [MERGE](
        context,
        { target, toMerge, properties }: { target: IPrivateContact; toMerge: IPrivateContact; properties: string }
    ): Promise<IMergeAsyncExecStatusResult> {
        const dataObject: IPrivateContact = {
            dobjId: target.dobjId,
            salutation: target.salutation ?? toMerge.salutation,
            phones: (target.phones ? removeDuplicate(target.phones.concat(toMerge.phones ?? [])).slice(0, 5) : toMerge.phones) ?? [],
            address:
                target.address?.country || target.address?.city?.length || target.address?.zip || target.address?.state || target.address?.address1
                    ? {
                          country: getDobjId(target.address?.country),
                          address1: target.address?.address1,
                          city: target.address?.city,
                          zip: target.address?.zip,
                          state: target.address?.state
                      }
                    : {
                          country: getDobjId(toMerge.address?.country),
                          address1: toMerge.address?.address1,
                          city: toMerge.address?.city,
                          zip: toMerge.address?.zip,
                          state: toMerge.address?.state
                      },
            linkedInUrl: target.linkedInUrl?.length ? target.linkedInUrl : toMerge.linkedInUrl,
            urls: target.urls?.length ? target.urls : toMerge.urls,
            description: target.description
                ? toMerge.description
                    ? `${target.description}<p></p>${toMerge.description}`
                    : target.description
                : toMerge.description
        };

        const statusId: string = await privateContactsManager.merge(dataObject, toMerge.dobjId, properties);
        return privateContactsManager.getExecutionManager().waitForStatus(statusId);
    },
    async [RETRIEVE_MATCHES](context, { contactId }: { contactId: number }): Promise<void> {
        const contact = context.getters[GET_BY_ID](contactId);
        // we retrieve the matches search result and extract only the matches that are not the original object
        const matches = (await privateContactsManager.retrieveMatches(contact.dobjPrettifiedBusinessWiseId as string, privateContactProperties.DETAILS))
            ?.map((el) => {
                return el.match;
            })
            .filter((match) => {
                return match?.dobjId !== contactId;
            });
        context.commit(SET_MATCHES, {
            contactId,
            matches
        });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IPrivateContact[] }) {
        const mainContactCompanies: IPrivateContactCompany[] = [];
        const contacts: IPrivateContact[] = [];
        dataObjectList.forEach((contact) => {
            let _contact = Object.assign({}, contact);

            if (contact.mainCompany !== null && contact.mainCompany !== undefined && isNaN(contact.mainCompany)) {
                const mainCompany = contact.mainCompany as unknown as IPrivateContactCompany;
                mainContactCompanies.push(mainCompany);
                _contact = Object.assign({}, contact, { mainCompany: mainCompany.dobjId });
            }

            contacts.push(_contact);
        });

        if (contacts.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: contacts });
        }
        if (mainContactCompanies.length > 0) {
            void context.dispatch(`${PRIVATE_CONTACT_COMPANY_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: mainContactCompanies }, { root: true });
        }
    }
};
